import React, { useEffect, useState, useMemo } from "react";
import styles from "./tasks-drawer.module.css";
import { Table, Tag, Tooltip, Input, Space, Avatar, Modal } from "antd";
import type { ColumnType } from "antd/es/table";
import { TTaskList } from "../../../../../types/task.type";
import useFetch from "../../../../../hooks/useFetch";
import {
  EyeOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { GetTasksListByStatus } from "../../../../../utils/api.constant";
import { ePriority } from "../../../../../utils/enum";
import { TDropdownOptions } from "../../../../../types/config";
import TaskDetailsModal from "../../../../../components/task-card/task-details";

interface TasksDrawerProps {
  projectId: string;
  statusId: number;
  projectName: string;
  statusName: string;
  onCloseDrawer: () => void;
  projectCategory: TDropdownOptions | null;
  tasks?: TTaskList[]; // Optional pre-loaded tasks
}

const TasksDrawer: React.FC<TasksDrawerProps> = ({
  projectId,
  statusId,
  projectName,
  statusName,
  onCloseDrawer,
  projectCategory,
  tasks: preloadedTasks,
}) => {
  const { post } = useFetch();
  const [tasks, setTasks] = useState<TTaskList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskId, setTaskId] = useState<number>(0);
  const [globalSearchText, setGlobalSearchText] = useState("");

  const fetchTasks = async () => {
    if (preloadedTasks && preloadedTasks.length > 0) {
      // Use preloaded tasks from parent component
      const formattedTasks = preloadedTasks.map((task: any) => ({
        taskCategoryId: task.taskCategoryId,
        taskCategoryName: task.taskCategoryName,
        taskDescription: task.taskDescription,
        assignedUser: task.assignedUser || [],
        employeeName:
          task.assignedUser && task.assignedUser.length > 0
            ? task.assignedUser.map((user: any) => user.fullName).join(", ")
            : "Unassigned",
        managerName: task.managerName || "N/A",
        estimatedTime: task.estimatedTime || "N/A",
        actualTime: task.actualTime || "N/A",
        priority: task.priority,
        startDate: task.startDate
          ? dayjs(task.startDate).format("MMM D, YYYY")
          : "N/A",
        endDate: task.endDate
          ? dayjs(task.endDate).format("MMM D, YYYY")
          : "N/A",
        timeLeft: task.timeLeft || "N/A",
        ticketNumber: task.ticketNumber || `T-${task.taskCategoryId}`,
      }));
      setTasks(formattedTasks);
      setIsLoading(false);
      return;
    }

    // Fallback to API call if no preloaded tasks
    setIsLoading(true);
    try {
      const response = await post(GetTasksListByStatus, {
        data: {
          projectId: projectId,
          statusId: statusId,
        },
      });

      const { data, dataResponse } = response;

      if (dataResponse.returnCode === 0) {
        const formattedTasks = data.map((task: any) => ({
          taskCategoryId: task.id,
          taskCategoryName: task.name,
          taskDescription: task.description,
          assignedUser: task.assignedUser || [],
          employeeName:
            task.assignedUser && task.assignedUser.length > 0
              ? task.assignedUser.map((user: any) => user.fullName).join(", ")
              : "Unassigned",
          managerName: task.managerName || "N/A",
          estimatedTime: task.estimatedTime || "N/A",
          actualTime: task.actualTime || "N/A",
          priority: task.priority,
          startDate: dayjs(task.startDateTime).format("MMM D, YYYY"),
          endDate: dayjs(task.endDateTime).format("MMM D, YYYY"),
          timeLeft: task.timeLeft || "N/A",
          ticketNumber: task?.ticketNumber || `T-${task.id}`,
        }));
        setTasks(formattedTasks);
      } else {
        console.error("Failed to fetch tasks:", dataResponse.description);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [projectId, statusId, preloadedTasks]);

  // Global search functionality
  const filteredTasks = useMemo(() => {
    if (!globalSearchText) return tasks;

    return tasks.filter((task) => {
      const searchFields = [
        task.taskCategoryName,
        task.taskDescription,
        task.employeeName,
        task.managerName,
        task.startDate,
        task.endDate,
      ]
        .join(" ")
        .toLowerCase();

      return searchFields.includes(globalSearchText.toLowerCase());
    });
  }, [tasks, globalSearchText]);

  // Get unique values for filter dropdowns
  const uniqueEmployees = useMemo(() => {
    const unique = Array.from(new Set(tasks.map((task) => task.employeeName)));
    return unique
      .filter((name) => name && name !== "Unassigned")
      .map((name) => ({
        text: name,
        value: name,
      }));
  }, [tasks]);

  const uniqueManagers = useMemo(() => {
    const unique = Array.from(new Set(tasks.map((task) => task?.managerName)));
    return unique
      .filter((name) => name && name !== "N/A")
      .map((name) => ({
        text: name,
        value: name,
      }));
  }, [tasks]);

  const priorityFilters = [
    { text: "High", value: ePriority.HIGH },
    { text: "Medium", value: ePriority.MEDIUM },
    { text: "Low", value: ePriority.LOW },
  ];

  const columns: ColumnType<TTaskList>[] = [
    {
      title: "Ticket #",
      dataIndex: "ticketNumber",
      key: "ticketNumber",
      width: 100,
      sorter: (a, b) =>
        (a.ticketNumber || "").localeCompare(b.ticketNumber || ""),
      render: (text: string) => (
        <span className={styles.ticketNumber}>{text}</span>
      ),
    },
    {
      title: "Task Name",
      dataIndex: "taskCategoryName",
      key: "taskCategoryName",
      width: 180,
      sorter: (a, b) => a.taskCategoryName.localeCompare(b.taskCategoryName),
      render: (text: string) => <span className={styles.taskName}>{text}</span>,
    },
    {
      title: "Description",
      dataIndex: "taskDescription",
      width: 200,
      key: "taskDescription",
      render: (text: string) => (
        <Tooltip title={text || "No description available"}>
          <div
            className={styles.description}
            dangerouslySetInnerHTML={{
              __html: text
                ? text.length > 50
                  ? `${text.slice(0, 50)}...`
                  : text
                : '<span class="' + styles.noDescription + '">-</span>',
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: "Assigned To",
      dataIndex: "assignedUser",
      key: "assignedUser",
      width: 200,
      filters: uniqueEmployees,
      onFilter: (value, record) =>
        record.employeeName.includes(value as string),
      sorter: (a, b) => a.employeeName.localeCompare(b.employeeName),
      render: (assignedUser: any[]) => {
        if (!assignedUser || assignedUser.length === 0) {
          return (
            <div className={styles.userCell}>
              <UserOutlined className={styles.userIcon} />
              <span>Unassigned</span>
            </div>
          );
        }

        if (assignedUser.length === 1) {
          return (
            <div className={styles.userCell}>
              <Avatar
                size='small'
                // style={{ backgroundColor: "#" }}
              >
                {assignedUser[0].fullName.charAt(0).toUpperCase()}
              </Avatar>
              <span>{assignedUser[0].fullName}</span>
            </div>
          );
        }

        // Multiple users
        return (
          <div className={styles.userCell}>
            <Avatar.Group
              maxCount={2}
              size='small'
            >
              {assignedUser.map((user: any, index: number) => (
                <Tooltip
                  key={user.id || index}
                  title={user.fullName}
                >
                  <Avatar style={{ backgroundColor: "#1890ff" }}>
                    {user.fullName.charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
            </Avatar.Group>
            <Tooltip
              title={
                <div>
                  {assignedUser.map((user: any) => (
                    <div key={user.id}>{user.fullName}</div>
                  ))}
                </div>
              }
            >
              <span style={{ marginLeft: "8px" }}>
                {assignedUser.length} members
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Assigned By",
      dataIndex: "managerName",
      key: "managerName",
      width: 140,
      filters: uniqueManagers,
      onFilter: (value, record) => record.managerName === value,
      sorter: (a, b) => a.managerName.localeCompare(b.managerName),
      render: (text: string) => (
        <div className={styles.userCell}>
          <UserOutlined className={styles.userIcon} />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: "Time",
      key: "time",
      width: 150,
      render: (_, record) => (
        <div className={styles.timeContainer}>
          <div className={styles.timeRow}>
            <ClockCircleOutlined className={styles.timeIcon} />
            <span className={styles.timeLabel}>Est:</span>
            <span className={styles.timeValue}>
              {record.estimatedTime !== "N/A"
                ? `${record.estimatedTime}m`
                : "N/A"}
            </span>
          </div>
          <div className={styles.timeRow}>
            <ClockCircleOutlined className={styles.timeIcon} />
            <span className={styles.timeLabel}>Act:</span>
            <span className={styles.timeValue}>
              {record.actualTime !== "N/A" ? `${record.actualTime}m` : "N/A"}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      filters: priorityFilters,
      onFilter: (value, record) => record.priority === value,
      sorter: (a, b) => a.priority - b.priority,
      render: (priority: number) => {
        const priorityMap: Record<
          ePriority,
          { label: string; className: string }
        > = {
          [ePriority.HIGH]: { label: "High", className: styles.priorityHigh },
          [ePriority.MEDIUM]: {
            label: "Medium",
            className: styles.priorityMedium,
          },
          [ePriority.LOW]: { label: "Low", className: styles.priorityLow },
        };

        const { label, className } = priorityMap[priority as ePriority] || {
          label: "N/A",
          className: "",
        };

        return (
          <span className={`${styles.priorityBadge} ${className}`}>
            {label}
          </span>
        );
      },
    },
    {
      title: "Timeline",
      key: "timeline",
      width: 150,
      render: (_, record) => (
        <div className={styles.timeline}>
          <div className={styles.dateRow}>
            <span className={styles.dateLabel}>Start:</span>
            <span className={styles.dateValue}>{record.startDate}</span>
          </div>
          <div className={styles.dateRow}>
            <span className={styles.dateLabel}>End:</span>
            <span className={styles.dateValue}>{record.endDate}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center" as "center",
      render: (_, record) => (
        <Tooltip title='View details'>
          <EyeOutlined
            onClick={() => {
              setTaskId(record?.taskCategoryId);
              setIsTaskModalOpen(true);
            }}
            className={styles.viewIcon}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      {/* Search Section */}
      <div className={styles.searchSection}>
        <Input
          placeholder='Search tasks...'
          prefix={<SearchOutlined style={{ color: "var( --bs-search-style)" }}/>}
          value={globalSearchText}
          onChange={(e) => setGlobalSearchText(e.target.value)}
          allowClear
          className={styles.searchInput}
        />
        {globalSearchText && (
          <div className={styles.searchResults}>
            Showing {filteredTasks.length} of {tasks.length} tasks
          </div>
        )}
      </div>

      {/* Table Section */}
      <div className={styles.tableSection}>
        {isLoading ? (
          <div className={styles.loaderContainer}>
            <img
              src='/webpm-loader.gif'
              alt='Loading...'
              className={styles.loader}
            />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredTasks}
            rowKey='taskCategoryId'
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} tasks`,
            }}
            scroll={{ x: true, y: 450 }}
            size='middle'
            className={styles.table}
          />
        )}
      </div>

      {/* Task Details Modal */}
      <Modal
        title='Task Details'
        open={isTaskModalOpen}
        onOk={() => {}}
        onCancel={() => setIsTaskModalOpen(false)}
        footer={null}
        width='70%'
      >
        {taskId ? (
          <TaskDetailsModal
            id={taskId}
            setIsModalOpen={setIsTaskModalOpen}
            selectedProject={projectId}
          />
        ) : (
          <p>No task selected.</p>
        )}
      </Modal>
    </div>
  );
};

export default TasksDrawer;
